import type { AuthSession, StaffUser } from "../services/types";

/**
 * Mock auth. Deliberately independent of the staff-user management list:
 * the staff list in Settings is sample data for demonstrating a screen, while
 * this mock keeps the application usable. Coupling the two meant a test action
 * on the user-management screen could break sign-in, so they are kept separate.
 *
 * Any correctly formatted email with a non-empty password signs in and returns
 * the default signed-in user, "Staff User" with the Super Admin role, so every
 * module, action and setting is unlocked during testing.
 */

const SESSION_KEY = "kameti.session.userId";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const DEFAULT_STAFF_USER: StaffUser = {
  id: "u-staff",
  name: "Staff User",
  email: "staff@kametipro.in",
  phone: "+91 98000 00000",
  role: "super_admin",
  avatarColor: "#0d9488",
  status: "active",
  lastLogin: null,
  createdAt: "2025-11-01T08:00:00.000Z",
};

function encodeToken(): string {
  return `mock-token-${btoa(DEFAULT_STAFF_USER.id)}-${Date.now()}`;
}

function simulateDelay(ms = 700): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

/** Accepts any correctly formatted email with a non-empty password. */
export async function mockLogin(email: string, password: string): Promise<AuthSession> {
  await simulateDelay(800);
  if (!email || !password) {
    throw new Error("Please enter your email and password.");
  }
  if (!EMAIL_RE.test(email.trim())) {
    throw new Error("Please enter a valid email address.");
  }

  localStorage.setItem(SESSION_KEY, DEFAULT_STAFF_USER.id);
  const session: AuthSession = { user: { ...DEFAULT_STAFF_USER }, token: encodeToken() };
  return session;
}

export async function mockGetCurrentUser(token: string): Promise<StaffUser | null> {
  await simulateDelay(150);
  if (!token) return null;
  return { ...DEFAULT_STAFF_USER };
}

export async function mockLogout(): Promise<void> {
  await simulateDelay(100);
  try {
    localStorage.removeItem(SESSION_KEY);
  } catch {
    // ignore
  }
}

export async function mockForgotPassword(email: string): Promise<void> {
  await simulateDelay(800);
  if (!email) {
    throw new Error("Please enter your email address.");
  }
}